import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, concatMap, forkJoin, map, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { EditorModule } from 'primeng/editor';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CompanyAccess } from '../../../../company/tenant-access.model';
import { TenantScopeService } from '../../../../company/tenant-scope.service';
import { CrmClient } from '../../../../crm/crm.model';
import { CrmService } from '../../../../crm/crm.service';
import { VehicleCapacity } from '../../../../fleet/vehicle/params/vehicle-capacity';
import { VehicleConfig } from '../../../../fleet/vehicle/params/vehicle-config';
import { VehicleType } from '../../../../fleet/vehicle/params/vehicle-type';
import { Country } from '../../../../models/common/country.model';
import { OrdersService } from '../../../../order/order.service';
import { PaymentTerm } from '../../../../order/models/payment-term.model';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { BaseMasterDataService } from '../../../../services/base-master-data.service';
import { CommercialPartyPickerComponent } from '../../../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../../../shared/commercial-party-picker/commercial-party.model';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { RentalAgreement, RentalRate } from '../../rental.model';
import { RentalService } from '../../rental.service';

@Component({
    selector: 'app-rental-agreement-form',
    standalone: true,
    templateUrl: './rental-agreement-form.component.html',
    styleUrls: ['./rental-agreement-form.component.css'],
    imports: [CommonModule, ReactiveFormsModule, ButtonModule, CommercialPartyPickerComponent, DatePickerModule, EditorModule, IftaLabelModule, InputGroupAddonModule, InputGroupModule, InputNumberModule, InputTextModule, MessageModule, ProgressBarModule, SelectModule, ToastModule, ToggleSwitchModule, PageShellComponent, PageHeroComponent, PageFormSectionComponent],
    providers: [MessageService]
})
export class RentalAgreementFormComponent implements OnInit {
    agreementId?: number;
    owningCompanyId?: number;
    loading = false;
    saving = false;
    readonly agreementForm: FormGroup;
    commercialParty?: CommercialPartyReference;
    paymentTerms: PaymentTerm[] = [];
    countries: Country[] = [];
    companyOptions: { label: string; value: number }[] = [];
    vehicleTypes: VehicleType[] = [];
    private systemTimezone = 'Asia/Riyadh';

    readonly transporterRoles = ['TRANSPORTER'];
    readonly structureOptions = [
        { label: 'Driver primary (vehicle included)', value: 'DRIVER_PRIMARY_BUNDLE' },
        { label: 'Separate driver and vehicle charges', value: 'SEPARATE_RESOURCES' },
        { label: 'Vehicle primary (driver included)', value: 'VEHICLE_WITH_DRIVER_BUNDLE' }
    ];
    readonly policyOptions = [{ label: 'Daily rental', value: 'DAILY_RENTAL' }, { label: 'Per trip', value: 'PER_TRIP' }, { label: 'Hybrid', value: 'HYBRID' }];
    readonly triggerOptions = [{ label: 'Delivery completed', value: 'WORK_COMPLETED' }];
    readonly settlementOptions = [{ label: 'Weekly', value: 'WEEKLY' }, { label: 'Monthly', value: 'MONTHLY' }];
    readonly crossMidnightOptions = [{ label: 'Charge start day', value: 'START_DAY' }, { label: 'Charge each workday', value: 'EACH_WORKDAY' }];

    constructor(
        private formBuilder: FormBuilder,
        private rentalService: RentalService,
        private crmService: CrmService,
        private orderService: OrdersService,
        private baseMasterData: BaseMasterDataService,
        private tenantScope: TenantScopeService,
        private session: SessionStorageService,
        private route: ActivatedRoute,
        private router: Router,
        private messages: MessageService
    ) {
        this.agreementForm = formBuilder.group({
            agreementNumber: [''],
            supplierName: ['', Validators.required],
            supplierCompanyId: [null as number | null],
            supplierPartyId: [null as number | null],
            supplierCrmClientId: [null as number | null, Validators.required],
            supplierCountryId: [null as number | null],
            costingCompanyId: [null as number | null],
            commercialStructure: ['DRIVER_PRIMARY_BUNDLE', Validators.required],
            costPolicy: ['DAILY_RENTAL', Validators.required],
            chargeTrigger: ['WORK_COMPLETED', Validators.required],
            settlementFrequency: ['MONTHLY', Validators.required],
            paymentTermId: [null as number | null],
            paymentTerms: [''],
            taxRate: [0],
            minimumCommitmentDays: [0],
            cancellationCutoffHours: [0],
            chargeWeekendDays: [false],
            chargeNoShow: [false],
            crossMidnightPolicy: ['START_DAY', Validators.required],
            approvalThresholdAmount: [null as number | null],
            currencyCode: ['SAR', Validators.required],
            effectiveStartDate: [new Date(), Validators.required],
            effectiveEndDate: [null as Date | null],
            notes: [''],
            initialVehicleTypeId: [null as number | null, Validators.required],
            initialVehicleConfigurationId: [null as number | null],
            initialVehicleCapacityId: [null as number | null],
            initialDailyRate: [null as number | null, [Validators.required, Validators.min(0)]]
        });
    }

    get isEditMode(): boolean { return this.agreementId != null; }
    get selectedVehicleType(): VehicleType | undefined { return this.vehicleTypes.find(type => type.id === this.agreementForm.get('initialVehicleTypeId')?.value); }
    get vehicleConfigurations(): VehicleConfig[] { return this.selectedVehicleType?.vehicleConfigs ?? []; }
    get vehicleCapacities(): VehicleCapacity[] { return this.selectedVehicleType?.vehicleCapacities ?? []; }
    get currencyOptions(): { label: string; value: string }[] {
        const codes = new Set(['SAR', this.agreementForm.get('currencyCode')?.value, ...this.countries.map(country => country.currencyCode)]);
        return [...codes].filter((code): code is string => !!code).sort().map(code => ({ label: code, value: code }));
    }

    ngOnInit(): void {
        this.owningCompanyId = this.session.getCompany()?.id;
        this.companyOptions = this.toCompanyOptions(this.tenantScope.getAccessibleCompanies(), this.owningCompanyId);
        this.vehicleTypes = this.session.getVehicleTypes() ?? [];
        this.loadReferenceData();
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (Number.isInteger(id) && id > 0) {
            this.agreementId = id;
            ['initialVehicleTypeId', 'initialVehicleConfigurationId', 'initialVehicleCapacityId', 'initialDailyRate'].forEach(controlName => {
                this.agreementForm.get(controlName)?.clearValidators();
                this.agreementForm.get(controlName)?.updateValueAndValidity({ emitEvent: false });
            });
            this.load();
        }
    }

    onCommercialPartyChange(value: CommercialPartyReference): void {
        this.commercialParty = value;
        const crmClientId = value.crmClientId ?? null;
        this.agreementForm.patchValue({
            supplierCrmClientId: crmClientId,
            supplierName: '',
            supplierCompanyId: null,
            supplierPartyId: null,
            supplierCountryId: null
        });
        if (!crmClientId) return;
        this.crmService.getClient(crmClientId).subscribe({
            next: response => this.applyTransporterClient(response.data),
            error: () => this.messages.add({ severity: 'error', summary: 'Rental', detail: 'Could not load the selected CRM transporter.' })
        });
    }

    onInitialVehicleTypeChange(): void {
        this.agreementForm.patchValue({ initialVehicleConfigurationId: null, initialVehicleCapacityId: null });
    }

    onPaymentTermChange(clearWhenNoSelection = false): void {
        const paymentTermId = this.agreementForm.get('paymentTermId')?.value as number | null;
        const paymentTerm = this.paymentTerms.find(term => term.paymentTermID === paymentTermId);
        if (paymentTerm) this.agreementForm.patchValue({ paymentTerms: paymentTerm.name });
        else if (clearWhenNoSelection) this.agreementForm.patchValue({ paymentTerms: '' });
    }

    save(): void {
        if (this.agreementForm.invalid) {
            this.agreementForm.markAllAsTouched();
            this.messages.add({ severity: 'warn', summary: 'Rental', detail: 'Select a CRM transporter and complete the required agreement fields.' });
            return;
        }
        const companyId = this.owningCompanyId;
        if (!companyId) return;
        const value = this.agreementForm.getRawValue();
        if (!this.isEditMode && (value.initialVehicleTypeId == null || value.initialDailyRate == null || value.initialDailyRate < 0)) {
            this.messages.add({ severity: 'warn', summary: 'Rental', detail: 'Select a vehicle type and enter its daily rate.' });
            return;
        }
        const { initialVehicleTypeId, initialVehicleConfigurationId, initialVehicleCapacityId, initialDailyRate, ...agreementValue } = value;
        const request: RentalAgreement = {
            ...agreementValue,
            owningCompanyId: companyId,
            tenantTimezone: this.systemTimezone,
            effectiveStartDate: this.isoDate(value.effectiveStartDate),
            effectiveEndDate: this.isoDate(value.effectiveEndDate)
        } as RentalAgreement;
        this.saving = true;
        let createdAgreementId: number | undefined;
        const action = this.isEditMode
            ? this.rentalService.updateAgreement(this.agreementId!, request)
            : this.rentalService.createAgreement(request).pipe(concatMap(response => {
                const agreementId = response.data?.rentalAgreementId;
                if (!agreementId) throw new Error('The rental agreement was created without an identifier.');
                createdAgreementId = agreementId;
                const rateCard: RentalRate = {
                    rateScope: 'VEHICLE_TYPE', resourceType: 'VEHICLE', vehicleTypeId: initialVehicleTypeId,
                    vehicleConfigurationId: initialVehicleConfigurationId, vehicleCapacityId: initialVehicleCapacityId,
                    rateComponent: 'BASE_DAY', amount: initialDailyRate, currencyCode: value.currencyCode, unit: 'DAY', active: true,
                    effectiveStartDate: this.isoDate(value.effectiveStartDate), effectiveEndDate: this.isoDate(value.effectiveEndDate)
                };
                return this.rentalService.addRateCard(agreementId, rateCard).pipe(map(() => response));
            }));
        action.subscribe({
            next: response => {
                this.saving = false;
                const savedId = response.data?.rentalAgreementId ?? this.agreementId;
                this.messages.add({ severity: 'success', summary: 'Rental', detail: this.isEditMode ? 'Rental agreement updated.' : 'Rental agreement and initial vehicle-type rate card created.' });
                this.router.navigate(savedId ? ['/fleet/rentals/agreements', savedId] : ['/fleet/rentals/agreements']);
            },
            error: error => {
                this.saving = false;
                const detail = error?.error?.message || error?.message || 'Could not save the rental agreement.';
                this.messages.add({ severity: createdAgreementId ? 'warn' : 'error', summary: 'Rental', detail: createdAgreementId ? `Agreement was created, but the initial vehicle-type rate card needs attention: ${detail}` : detail });
                if (createdAgreementId) this.router.navigate(['/fleet/rentals/agreements', createdAgreementId]);
            }
        });
    }

    cancel(): void { this.router.navigate(this.agreementId ? ['/fleet/rentals/agreements', this.agreementId] : ['/fleet/rentals/agreements']); }
    isInvalid(controlName: string): boolean { const control = this.agreementForm.get(controlName); return !!control && control.invalid && (control.dirty || control.touched); }

    private loadReferenceData(): void {
        forkJoin({
            paymentTerms: this.orderService.getAllPaymentTerms().pipe(catchError(() => of([] as PaymentTerm[]))),
            countries: this.baseMasterData.getCountries().pipe(catchError(() => of([] as Country[])))
        }).subscribe(({ paymentTerms, countries }) => {
            this.paymentTerms = paymentTerms || [];
            this.countries = countries || [];
            const currentPaymentTermName = this.agreementForm.get('paymentTerms')?.value as string | null;
            if (!this.agreementForm.get('paymentTermId')?.value && currentPaymentTermName) {
                const matchingTerm = this.paymentTerms.find(term => term.name?.trim().toLowerCase() === currentPaymentTermName.trim().toLowerCase());
                if (matchingTerm) this.agreementForm.patchValue({ paymentTermId: matchingTerm.paymentTermID });
            }
            this.onPaymentTermChange();
        });
    }

    private load(): void {
        this.loading = true;
        this.rentalService.getAgreement(this.agreementId!).subscribe({
            next: response => {
                const agreement = response.data;
                this.systemTimezone = agreement?.tenantTimezone || this.systemTimezone;
                this.agreementForm.patchValue({
                    ...agreement,
                    chargeTrigger: 'WORK_COMPLETED',
                    effectiveStartDate: this.asLocalDate(agreement?.effectiveStartDate),
                    effectiveEndDate: this.asLocalDate(agreement?.effectiveEndDate)
                });
                this.commercialParty = agreement?.supplierCrmClientId ? { source: 'CRM', crmClientId: agreement.supplierCrmClientId } : undefined;
                this.loading = false;
            },
            error: error => { this.loading = false; this.messages.add({ severity: 'error', summary: 'Rental', detail: error?.error?.message || 'Could not load the rental agreement.' }); }
        });
    }

    private applyTransporterClient(client?: CrmClient): void {
        if (!client?.id || client.id !== this.agreementForm.get('supplierCrmClientId')?.value) return;
        const countryCurrency = this.countries.find(country => country.id === client.countryId)?.currencyCode;
        this.agreementForm.patchValue({
            supplierName: client.companyName,
            supplierCompanyId: client.linkedCompanyId ?? null,
            supplierPartyId: client.linkedCompanyId ?? null,
            supplierCountryId: client.countryId ?? null,
            currencyCode: client.currencyCode || countryCurrency || this.agreementForm.get('currencyCode')?.value,
            paymentTermId: client.defaultPaymentTermId ?? this.agreementForm.get('paymentTermId')?.value
        });
        this.onPaymentTermChange();
    }

    private toCompanyOptions(companies: CompanyAccess[], currentCompanyId?: number): { label: string; value: number }[] {
        const options = companies.map(company => ({ label: company.companyName, value: company.companyId }));
        const current = this.session.getCompany();
        if (currentCompanyId && !options.some(option => option.value === currentCompanyId)) options.unshift({ label: current?.name || `Company ${currentCompanyId}`, value: currentCompanyId });
        return options;
    }
    private isoDate(value: Date | null | undefined): string | null { return value ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}` : null; }
    private asLocalDate(value?: string | null): Date | null { return value ? new Date(`${value}T00:00:00`) : null; }
}
